import React, { useState, useRef } from "react";
import { http, formatErr } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { DownloadSimple, UploadSimple, X, CheckCircle, WarningCircle, Spinner } from "@phosphor-icons/react";

/**
 * Reusable bulk-import toolbar: a "Template" download button + an "Import CSV"
 * button that opens a modal, uploads the file, and shows an import summary
 * (imported count + skipped rows with reasons). Rows reference related records
 * by NAME (e.g. store name) — bad rows are skipped, good rows imported.
 */
export function ImportCsv({ resource, invalidateKeys = [] }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [fileName, setFileName] = useState("");

  const downloadTemplate = async () => {
    try {
      const r = await http.get(`/import/template/${resource}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${resource}-import-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      alert(formatErr(ex?.response?.data?.detail));
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post(`/import/${resource}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openModal = () => { setOpen(true); setResult(null); setErr(""); setFileName(""); };
  const close = () => { setOpen(false); setResult(null); setErr(""); setFileName(""); };

  return (
    <>
      <button onClick={downloadTemplate} data-testid={`import-${resource}-template-btn`}
        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors">
        <DownloadSimple size={14} weight="bold" /> Template
      </button>
      <button onClick={openModal} data-testid={`import-${resource}-open-btn`}
        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors">
        <UploadSimple size={14} weight="bold" /> Import CSV
      </button>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid={`import-${resource}-modal`}>
          <div className="bg-white border border-slate-200 rounded-md w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Bulk import</div>
                <h2 className="text-xl font-heading font-semibold tracking-tight mt-1 capitalize">{resource.replace("-", " ")}</h2>
              </div>
              <button type="button" onClick={close} data-testid={`import-${resource}-close-btn`} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>

            <div className="text-sm text-slate-600 space-y-2">
              <p>Upload a CSV file. Linked records (e.g. store, company, brand, category) are matched by <strong>name</strong>. Invalid rows are skipped — you'll see a summary below.</p>
              <button onClick={downloadTemplate} className="text-[#002FA7] hover:underline inline-flex items-center gap-1 text-sm">
                <DownloadSimple size={13} weight="bold" /> Download the template first
              </button>
            </div>

            <label className="block">
              <span className="overline mb-1.5 block">CSV file</span>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onPick} disabled={busy}
                data-testid={`import-${resource}-file-input`}
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border file:border-slate-200 file:text-sm file:font-medium file:bg-slate-50 hover:file:bg-slate-100 file:cursor-pointer" />
            </label>

            {busy && (
              <div className="flex items-center gap-2 text-sm text-slate-600" data-testid={`import-${resource}-busy`}>
                <Spinner size={16} className="animate-spin" /> Importing {fileName}…
              </div>
            )}

            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1" data-testid={`import-${resource}-error`}>{err}</div>}

            {result && (
              <div className="space-y-3" data-testid={`import-${resource}-result`}>
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                  <span><strong className="font-mono-data">{result.imported}</strong> imported · <strong className="font-mono-data">{result.skipped?.length || 0}</strong> skipped · {result.total_rows} rows total</span>
                </div>
                {result.skipped?.length > 0 && (
                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <div className="bg-amber-50 px-3 py-2 text-[11px] font-heading uppercase tracking-wider text-amber-700 flex items-center gap-1.5 border-b border-slate-200">
                      <WarningCircle size={12} weight="fill" /> Skipped rows
                    </div>
                    <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                      {result.skipped.map((s) => (
                        <div key={`row-${s.row}`} className="px-3 py-1.5 text-xs flex items-center gap-2">
                          <span className="font-mono-data text-slate-400 shrink-0">Row {s.row}</span>
                          <span className="text-slate-700">{s.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={close} data-testid={`import-${resource}-done-btn`} className="h-10 px-4 text-sm border border-slate-200 rounded-md hover:bg-slate-50">{result ? "Done" : "Cancel"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
